<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" md="3" lg="3">
        <v-card class="mb-2" v-if="alumnoInfo">
          <v-card-text class="py-2">
            <div><strong>{{ alumnoInfo.nombre }}</strong></div>
            <div>DNI: {{ alumnoInfo.dni }} - Celular: {{ alumnoInfo.celular }}</div>
          </v-card-text>
        </v-card>
        <v-card>
          <v-card-title>Clases Disponibles</v-card-title>
          <v-card-text>
            <div ref="externalEvents">
              <v-row dense class="pa-2">
                <v-col v-for="clase in clasesPredefinidas" :key="clase.numero" cols="12" class="pa-1">
                  <v-chip class="external-event text-center w-100" color="primary" dark label :data-title="clase.numero"
                    :data-descripcion="clase.descripcion" :data-duracion="clase.duracion" :data-id="clase.id">
                    {{ clase.numero }}. {{ clase.descripcion }}
                  </v-chip>
                </v-col>
              </v-row>
            </div>
            <v-alert type="info" variant="tonal" class="mt-4" density="compact">
              Programa las clases en orden del 1 al 15.
            </v-alert>
            <v-alert
              v-if="requiereConfirmacionPago"
              type="success"
              variant="tonal"
              icon="mdi-cash"
              class="mt-2"
              density="compact"
            >
              Alumno con pago pendiente. Desde la clase {{ claseDebePagar + 1 }} se solicitará confirmación para programar.
            </v-alert>
          </v-card-text>
        </v-card>
        <div class="d-flex justify-end mt-2">
          <v-btn color="primary" @click="guardarProgramacion" block>
            Guardar Programación
          </v-btn>
        </div>
      </v-col>

      <v-col cols="12" md="9" lg="9">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Clases Programadas</span>
          </v-card-title>
          <v-card-text>
            <div class="calendar">
              <FullCalendar ref="calendar" :options="calendarOptions">
                <template #eventContent="{ event, timeText }">
                  <div class="event-content d-flex flex-column" :title="generarTooltip(event)">
                    <div class="event-info">
                      <div><b>{{ timeText }}</b></div>
                      <div class="d-flex align-center" style="flex-wrap: wrap;">
                        <span v-if="event.extendedProps.readOnly">
                          {{ event.title }} <i>({{ event.extendedProps.duracion }}m)</i>
                        </span>
                        <span v-else>
                          <i>Clase {{ event.extendedProps.numero }}</i>
                        </span>
                        <v-chip
                          v-if="event.extendedProps.circuito"
                          size="x-small"
                          density="compact"
                          color="red-darken-2"
                          text-color="white"
                          class="ml-1 px-1"
                          label
                        >
                          C
                        </v-chip>
                        <v-chip
                          v-if="event.extendedProps.generadoPorInasistencias"
                          size="x-small"
                          density="compact"
                          color="orange-darken-3"
                          text-color="white"
                          class="ml-1 px-1"
                          label
                          title="Asistencia generada automáticamente por inasistencia (2da cancelación del alumno)"
                        >
                          INASISTENCIA
                        </v-chip>
                      </div>
                    </div>
                    
                    <div v-if="!event.extendedProps.readOnly" class="event-actions mt-auto d-flex flex-wrap" style="gap: 2px;">
                      <v-btn
                        v-if="!event.extendedProps.asistenciaRegistrada"
                        icon
                        density="compact"
                        style="width: 20px; height: 20px;"
                        variant="text"
                        color="indigo"
                        @click.stop="abrirFirma(event)"
                      >
                        <v-icon size="14">mdi-draw-pen</v-icon>
                      </v-btn>
                      <v-btn
                        v-else
                        icon
                        density="compact"
                        style="width: 20px; height: 20px;"
                        variant="text"
                        color="red-darken-2"
                        :loading="asistenciaDeletingId === (event.extendedProps.id || event.id || event.extendedProps.programacionId)"
                        @click.stop="verFirma(event)"
                      >
                        <v-icon size="14">mdi-signature-freehand</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="event.extendedProps.programacionId || event.extendedProps.id"
                        icon
                        density="compact"
                        style="width: 20px; height: 20px;"
                        variant="text"
                        :color="event.extendedProps.kilometrajeCompleto ? 'green-darken-2' : 'orange-darken-2'"
                        @click.stop="abrirKilometraje(event)"
                      >
                        <v-icon size="14">mdi-speedometer</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        density="compact"
                        style="width: 20px; height: 20px;"
                        variant="text"
                        :color="event.extendedProps.circuito ? 'red-darken-2' : undefined"
                        @click.stop="toggleCircuito(event)"
                      >
                        <v-icon size="14">mdi-flag-checkered</v-icon>
                      </v-btn>
                      <v-btn icon density="compact" style="width: 20px; height: 20px;" variant="text" @click.stop="abrirDialogoDuracion(event)">
                        <v-icon size="14">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="event.extendedProps.programacionId && !event.extendedProps.asistenciaRegistrada"
                        icon
                        density="compact"
                        style="width: 20px; height: 20px;"
                        variant="text"
                        color="red"
                        :loading="cancelacionLoading && cancelacionProgramacionId === event.extendedProps.programacionId"
                        @click.stop="abrirCancelacion(event)"
                      >
                        <v-icon size="14">mdi-cancel</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </template>
              </FullCalendar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar.show" :timeout="snackbar.timeout" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>

    <v-dialog v-model="dialogoDuracion" max-width="300px">
      <v-card>
        <v-card-title>Editar duración</v-card-title>
        <v-card-text>
          <v-text-field v-model="nuevaDuracion" label="Duración (min)" type="number" min="30" max="120" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogoDuracion = false">Cancelar</v-btn>
          <v-btn text color="primary" @click="actualizarDuracion">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogConfirmacionPago" max-width="430">
      <v-card>
        <v-card-title class="text-h6">Alumno con pago pendiente</v-card-title>
        <v-card-text>
          El alumno superó su límite de clases antes de pagar.
          Si deseas continuar, confirma para programar la clase.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelarProgramacionPendiente">Cancelar</v-btn>
          <v-btn color="warning" @click="confirmarProgramacionPendiente">Confirmar y programar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogFirma" max-width="820" persistent>
      <v-card>
        <v-card-title class="text-h6 d-flex align-center ga-2">
          <v-icon color="indigo">mdi-draw-pen</v-icon>
          Registrar firma de asistencia
        </v-card-title>
        <v-card-text>
          <div class="border rounded pa-2 mb-3">
            <canvas ref="signatureCanvas" width="760" height="180" class="signature-canvas" />
          </div>
          <div class="d-flex ga-2 flex-wrap">
            <v-btn color="primary" variant="tonal" prepend-icon="mdi-tablet" @click="iniciarTopaz">Iniciar firma</v-btn>
            <v-btn color="warning" variant="tonal" prepend-icon="mdi-eraser" @click="limpiarFirma">Limpiar</v-btn>
          </div>
          <v-alert type="info" variant="tonal" class="mt-3 mb-0" density="comfortable">
            <v-icon start size="18">mdi-clock-outline</v-icon>
            Hora de asistencia a guardar: <strong>{{ formatDateTime(firmaPayload.fecha_hora) }}</strong>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cerrarFirma">Cancelar</v-btn>
          <v-btn color="indigo" :loading="firmaLoading" @click="guardarFirma" prepend-icon="mdi-content-save">Guardar firma</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogKilometraje" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-h6 d-flex align-center ga-2">
          <v-icon color="orange-darken-2">mdi-speedometer</v-icon>
          Kilometraje de la clase
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model.number="kilometrajePayload.kilometraje_inicial"
                label="Kilometraje inicial"
                type="number"
                min="0"
                suffix="km"
                prepend-inner-icon="mdi-counter"
                :rules="[reglaKilometrajeInicial]"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model.number="kilometrajePayload.kilometraje_final"
                label="Kilometraje final (opcional)"
                type="number"
                min="0"
                suffix="km"
                prepend-inner-icon="mdi-counter"
                hint="Puedes registrarlo al terminar la clase"
                persistent-hint
                :rules="[reglaKilometrajeFinal]"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cerrarKilometraje">Cancelar</v-btn>
          <v-btn color="orange-darken-2" :loading="kilometrajeLoading" @click="guardarKilometraje" prepend-icon="mdi-content-save">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogFirmaPreview" max-width="860">
      <v-card>
        <v-card-title class="text-h6 d-flex align-center ga-2">
          <v-icon color="red-darken-2">mdi-signature-freehand</v-icon>
          Firma registrada
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" class="mb-3">
            <div><strong>Instructor:</strong> {{ firmaPreviewDetalle.instructor }}</div>
            <div><strong>Vehículo:</strong> {{ firmaPreviewDetalle.vehiculo }}</div>
            <div><strong>Fecha y hora:</strong> {{ firmaPreviewDetalle.fechaHora }}</div>
          </v-alert>
          <v-skeleton-loader
            v-if="firmaPreviewLoading"
            type="image"
            class="mx-auto"
            max-width="780"
          />
          <v-alert v-else-if="firmaPreviewError" type="warning" variant="tonal" class="mb-0">
            {{ firmaPreviewError }}
          </v-alert>
          <img
            v-else-if="firmaPreviewUrl"
            :src="firmaPreviewUrl"
            alt="Firma de asistencia"
            class="firma-preview"
          >
          <v-alert v-else type="info" variant="tonal" class="mb-0">
            No se encontró la imagen de la firma para esta asistencia.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cerrarFirmaPreview">Cerrar</v-btn>
          <v-btn
            color="error"
            prepend-icon="mdi-delete"
            :loading="asistenciaDeletingId === firmaPreviewProgramacionId"
            :disabled="!firmaPreviewAsistenciaId"
            @click="eliminarAsistenciaPreview"
          >
            Eliminar firma
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogInasistencia" max-width="450" persistent>
      <v-card>
        <v-card-title class="text-h6 d-flex align-center ga-2">
          <v-icon color="orange-darken-3">mdi-alert</v-icon>
          Cancelaciones del alumno
        </v-card-title>
        <v-card-text>
          Este alumno canceló más de 2 veces.
          ¿Desea crear una asistencia generada por inasistencia?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="responderInasistencia(false)">No</v-btn>
          <v-btn color="orange-darken-3" @click="responderInasistencia(true)">Sí</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogCancelacion" max-width="450" persistent>
      <v-card>
        <v-card-title class="text-h6 d-flex align-center ga-2">
          <v-icon color="red">mdi-cancel</v-icon>
          Cancelar clase programada
        </v-card-title>
        <v-card-text>
          <v-select
            v-model="cancelacionMotivo"
            :items="motivosCancelacion"
            item-title="descripcion"
            item-value="id"
            label="Motivo de cancelación"
            :rules="[(v) => !!v || 'Selecciona un motivo']"
            class="mb-2"
          />
          <v-textarea
            v-model="cancelacionComentario"
            label="Comentario (opcional)"
            rows="2"
            auto-grow
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cerrarCancelacion">Volver</v-btn>
          <v-btn
            color="red"
            :loading="cancelacionLoading"
            :disabled="!cancelacionMotivo"
            @click="confirmarCancelacion"
          >
            Cancelar clase
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import { defineComponent, ref, onMounted, nextTick, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin, { Draggable } from '@fullcalendar/interaction'
import esLocale from '@fullcalendar/core/locales/es'

const parseBackendDate = (str) => {
  if (!str) return new Date()
  const d = new Date(str) // 12:00Z
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000) // 07:00 local
}

const toUTCString = (date) => date.toISOString()

export default defineComponent({
  components: { FullCalendar },
  setup() {
    const calendarRef = ref(null)
    const externalEvents = ref(null)

    const store = useStore()
    const route = useRoute()

    const eventos = ref([])
    const clasesPredefinidas = ref([])
    let clasesCurso = []
    const alumnoInfo = ref(null)

    // Cuando se cancela una programación y desaparece del calendario, la
    // clase queda "disponible" otra vez para reprogramarse. La reinsertamos
    // en clasesPredefinidas con su mismo número y la lista se reordena
    // ascendentemente para que el FIFO de programarClase la tome correctamente.
    const liberarClasePredefinida = (event) => {
      if (!event || event.numero == null) return
      const numero = String(event.numero)
      if (clasesPredefinidas.value.some((c) => c.numero === numero)) return
      clasesPredefinidas.value.push({
        numero,
        id: event.claseId,
        descripcion: event.descripcion || '',
        duracion: String(event.duracion ?? ''),
      })
      clasesPredefinidas.value.sort(
        (a, b) => Number(a.numero) - Number(b.numero),
      )
    }

    const snackbar = ref({ show: false, message: '', color: 'success', timeout: 3000 })
    const mostrarSnackbar = (msg, color = 'success', timeout = 3000) => {
      snackbar.value = { show: true, message: msg, color, timeout }
    }

    const hayConflicto = (start, end, excluido = null) =>
      eventos.value.some((ev) => {
        if (excluido && ev.originalStart === excluido) return false
        const evStart = new Date(ev.start).getTime()
        const evEnd = new Date(ev.end).getTime()
        return start < evEnd && end > evStart
      })



    const dialogoDuracion = ref(false)
    const dialogConfirmacionPago = ref(false)
    const eventoSeleccionado = ref(null)
    const nuevaDuracion = ref(45)
    const requiereConfirmacionPago = ref(false)
    const claseDebePagar = ref(0)
    const programacionPendiente = ref(null)
    const dialogFirma = ref(false)
    const firmaLoading = ref(false)
    const asistenciaDeletingId = ref(null)
    const signatureCanvas = ref(null)
    const topazTimer = ref(null)
    const firmaClockTimer = ref(null)
    const firmaPayload = ref({
      programacion_id: null,
      auto_id: 1,
      instructor_id: 1,
      fecha_hora: null,
    })
    const dialogKilometraje = ref(false)
    const kilometrajeLoading = ref(false)
    const kilometrajePayload = ref({
      programacion_id: null,
      kilometraje_inicial: null,
      kilometraje_final: null,
    })
    const dialogFirmaPreview = ref(false)
    const firmaPreviewLoading = ref(false)
    const firmaPreviewUrl = ref('')
    const firmaPreviewError = ref('')
    const firmaPreviewAsistenciaId = ref(null)
    const firmaPreviewProgramacionId = ref(null)
    const firmaPreviewDetalle = ref({
      instructor: 'No disponible',
      vehiculo: 'No disponible',
      fechaHora: 'No disponible',
    })

    const formatDateTime = (value) => {
      if (!value) return 'No disponible'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return String(value)

      return parsed.toLocaleString('es-PE', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    }

    const obtenerTexto = (...values) => {
      for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim()
        if (typeof value === 'number') return String(value)
      }
      return 'No disponible'
    }

    const construirDetalleAsistencia = ({ asistencia = {}, fallback = {} } = {}) => {
      const instructor = asistencia?.instructor || fallback?.instructor || {}
      const vehiculo = asistencia?.auto || asistencia?.vehiculo || fallback?.vehiculo || {}

      return {
        instructor: obtenerTexto(
          instructor?.nombre_completo,
          [instructor?.nombres, instructor?.apellido_paterno, instructor?.apellido_materno].filter(Boolean).join(' '),
          instructor?.nombre,
          asistencia?.instructor_nombre,
          fallback?.instructor
        ),
        vehiculo: obtenerTexto(
          vehiculo?.placa,
          vehiculo?.descripcion,
          vehiculo?.nombre,
          asistencia?.vehiculo,
          asistencia?.auto_placa,
          fallback?.vehiculo,
          asistencia?.auto_id
        ),
        fechaHora: formatDateTime(
          asistencia?.fecha_hora ||
          fallback?.fechaHora
        ),
      }
    }

    const abrirDialogoDuracion = (event) => {
      if (event.extendedProps.readOnly) return
      eventoSeleccionado.value = event
      const idx = eventos.value.findIndex((e) => e.originalStart === event.extendedProps.originalStart)
      if (idx !== -1) {
        nuevaDuracion.value = eventos.value[idx].duracion || 45
        dialogoDuracion.value = true
      }
    }

    const generarTooltip = (event) => {
      const props = event.extendedProps
      if (props.readOnly) {
        return `Alumno: ${event.title}\nClase: ${props.descripcion || 'Sin descripción'}\nDuración: ${props.duracion} minutos`
      } else {
        return `Clase: ${props.descripcion || 'Sin descripción'}\nDuración: ${props.duracion} minutos`
      }
    }

    const actualizarDuracion = () => {
      const idx = eventos.value.findIndex(
        (e) => e.originalStart === eventoSeleccionado.value.extendedProps.originalStart
      )
      if (idx !== -1) {
        const start = new Date(eventos.value[idx].start)
        start.setSeconds(0, 0)
        const newEnd = new Date(start.getTime() + nuevaDuracion.value * 60000)

        if (hayConflicto(start.getTime(), newEnd.getTime(), eventos.value[idx].originalStart)) {
          mostrarSnackbar('Este horario entra en conflicto con otra clase.', 'error')
          return
        }

        eventos.value[idx].start = toUTCString(start)
        eventos.value[idx].end = toUTCString(newEnd)
        eventos.value[idx].duracion = nuevaDuracion.value

        eventoSeleccionado.value.setStart(start)
        eventoSeleccionado.value.setEnd(newEnd)

        mostrarSnackbar('Duración actualizada.')
        dialogoDuracion.value = false
      }
    }

    const toggleCircuito = (event) => {
      if (event.extendedProps.readOnly) return

      const idx = eventos.value.findIndex((e) => e.originalStart === event.extendedProps.originalStart)
      if (idx === -1) return

      const nuevoValor = !eventos.value[idx].circuito
      eventos.value[idx].circuito = nuevoValor
      event.setExtendedProp('circuito', nuevoValor)
    }


    const ensureTopazScript = async () => {
      if (typeof window.GetSigImageB64 === 'function') return
      await new Promise((resolve, reject) => {
        const existingScript = document.querySelector('script[data-topaz="sigweb"]')
        if (existingScript) {
          existingScript.addEventListener('load', resolve, { once: true })
          existingScript.addEventListener('error', reject, { once: true })
          return
        }

        const script = document.createElement('script')
        script.src = 'https://www.sigplusweb.com/SigWebTablet.js'
        script.dataset.topaz = 'sigweb'
        script.onload = resolve
        script.onerror = reject
        document.body.appendChild(script)
      })
    }

    const iniciarTopaz = () => {
      if (!signatureCanvas.value || typeof window.SetTabletState !== 'function') return

      const ctx = signatureCanvas.value.getContext('2d')
      window.SetDisplayXSize(760)
      window.SetDisplayYSize(180)
      window.SetTabletState(0, topazTimer.value)
      window.SetJustifyMode(0)
      window.ClearTablet()

      if (topazTimer.value == null) {
        topazTimer.value = window.SetTabletState(1, ctx, 50)
      } else {
        window.SetTabletState(0, topazTimer.value)
        topazTimer.value = window.SetTabletState(1, ctx, 50)
      }
    }

    const limpiarFirma = () => {
      if (typeof window.ClearTablet === 'function') {
        window.ClearTablet()
      }
      if (signatureCanvas.value) {
        const ctx = signatureCanvas.value.getContext('2d')
        ctx.clearRect(0, 0, signatureCanvas.value.width, signatureCanvas.value.height)
      }
    }

    const getFirmaB64 = () => new Promise((resolve, reject) => {
      if (typeof window.NumberOfTabletPoints !== 'function' || window.NumberOfTabletPoints() === 0) {
        reject(new Error('Debe registrar una firma antes de guardar.'))
        return
      }

      window.SetTabletState(0, topazTimer.value)
      window.SetImageXSize(760)
      window.SetImageYSize(180)
      window.SetImagePenWidth(5)
      window.GetSigImageB64((str) => {
        if (!str) {
          reject(new Error('No se pudo obtener la imagen de la firma.'))
          return
        }
        resolve(str)
      })
    })

    const base64ToBlob = (base64, mimeType) => {
      const bytes = atob(base64)
      const arr = new Uint8Array(bytes.length)
      for (let i = 0; i < bytes.length; i += 1) {
        arr[i] = bytes.charCodeAt(i)
      }
      return new Blob([arr], { type: mimeType })
    }

    const iniciarRelojFirma = () => {
      if (firmaClockTimer.value) {
        clearInterval(firmaClockTimer.value)
      }
      firmaPayload.value.fecha_hora = new Date().toISOString()
      firmaClockTimer.value = setInterval(() => {
        firmaPayload.value.fecha_hora = new Date().toISOString()
      }, 1000)
    }

    const detenerRelojFirma = () => {
      if (!firmaClockTimer.value) return
      clearInterval(firmaClockTimer.value)
      firmaClockTimer.value = null
    }

    const abrirFirma = async (event) => {
      const programacionId = event.extendedProps?.id || event.id || event.extendedProps?.programacionId
      if (!programacionId) {
        mostrarSnackbar('Primero guarda la programación para generar el ID antes de firmar.', 'warning')
        return
      }

      firmaPayload.value = {
        programacion_id: Number(programacionId),
        auto_id: 1,
        instructor_id: 1,
        fecha_hora: new Date().toISOString(),
      }
      iniciarRelojFirma()
      dialogFirma.value = true

      try {
        await ensureTopazScript()
      } catch (error) {
        mostrarSnackbar('No se pudo cargar Topaz SigWeb en este navegador', 'warning')
      }

      nextTick(() => {
        iniciarTopaz()
      })
    }

    const cerrarFirma = () => {
      if (typeof window.SetTabletState === 'function') {
        window.SetTabletState(0, topazTimer.value)
      }
      topazTimer.value = null
      detenerRelojFirma()
      dialogFirma.value = false
      firmaPayload.value = {
        programacion_id: null,
        auto_id: 1,
        instructor_id: 1,
        fecha_hora: null,
      }
    }

    const abrirKilometraje = (event) => {
      const programacionId = event.extendedProps?.id || event.id || event.extendedProps?.programacionId
      if (!programacionId) {
        mostrarSnackbar('Primero guarda la programación para registrar el kilometraje.', 'warning')
        return
      }
      kilometrajePayload.value = {
        programacion_id: Number(programacionId),
        kilometraje_inicial: event.extendedProps?.kilometrajeInicial ?? null,
        kilometraje_final: event.extendedProps?.kilometrajeFinal ?? null,
      }
      dialogKilometraje.value = true
    }

    const cerrarKilometraje = () => {
      dialogKilometraje.value = false
      kilometrajePayload.value = {
        programacion_id: null,
        kilometraje_inicial: null,
        kilometraje_final: null,
      }
    }

    const guardarKilometraje = async () => {
      const payload = kilometrajePayload.value
      if (!payload.programacion_id) {
        mostrarSnackbar('No existe programación válida para registrar el kilometraje.', 'warning')
        return
      }
      if (reglaKilometrajeInicial(payload.kilometraje_inicial) !== true ||
        reglaKilometrajeFinal(payload.kilometraje_final) !== true) {
        mostrarSnackbar('Revise el kilometraje antes de guardar.', 'warning')
        return
      }

      kilometrajeLoading.value = true
      try {
        const body = { kilometraje_inicial: payload.kilometraje_inicial }
        if (payload.kilometraje_final !== null && payload.kilometraje_final !== '') {
          body.kilometraje_final = payload.kilometraje_final
        }

        const { data } = await axios.post(
          `/api/programacion/${payload.programacion_id}/kilometraje`,
          body
        )

        const asistencia = data?.asistencia || data?.data || data || {}
        const kmIni = asistencia?.kilometraje_inicial ?? payload.kilometraje_inicial
        const kmFin = asistencia?.kilometraje_final ?? (
          payload.kilometraje_final === '' ? null : payload.kilometraje_final
        )

        const idx = eventos.value.findIndex(
          (ev) => Number(ev.id || ev.programacionId) === Number(payload.programacion_id)
        )
        if (idx !== -1) {
          eventos.value[idx].kilometrajeInicial = kmIni
          eventos.value[idx].kilometrajeFinal = kmFin
          eventos.value[idx].kilometrajeCompleto = kmIni != null && kmFin != null
        }

        mostrarSnackbar('Kilometraje guardado correctamente.', 'success')
        cerrarKilometraje()
      } catch (error) {
        const msg = error?.response?.data?.message
          || Object.values(error?.response?.data?.errors || {})?.[0]?.[0]
          || 'No se pudo guardar el kilometraje.'
        mostrarSnackbar(msg, 'error')
      } finally {
        kilometrajeLoading.value = false
      }
    }

    const reglaKilometrajeInicial = (v) => {
      if (v === null || v === '' || v === undefined) return 'Ingrese el kilometraje inicial'
      if (Number(v) < 0) return 'Debe ser mayor o igual a 0'
      return true
    }

    const reglaKilometrajeFinal = (v) => {
      // El final es opcional: se puede registrar al terminar la clase.
      if (v === null || v === '' || v === undefined) return true
      if (Number(v) < 0) return 'Debe ser mayor o igual a 0'
      const inicial = kilometrajePayload.value.kilometraje_inicial
      if (inicial !== null && inicial !== '' && Number(v) < Number(inicial)) {
        return 'No puede ser menor que el kilometraje inicial'
      }
      return true
    }

    const guardarFirma = async () => {
      if (!firmaPayload.value.programacion_id) {
        mostrarSnackbar('No existe programación válida para registrar la firma.', 'warning')
        return
      }

      firmaLoading.value = true
      try {
        if (typeof window.GetSigImageB64 !== 'function') {
          throw new Error('Topaz SigWeb no está disponible en este navegador.')
        }

        const firmaB64 = await getFirmaB64()
        const blob = base64ToBlob(firmaB64, 'image/bmp')
        const file = new File([blob], `firma_${Date.now()}.bmp`, { type: 'image/bmp' })
        const formData = new FormData()
        formData.append('programacion_id', firmaPayload.value.programacion_id)
        formData.append('auto_id', firmaPayload.value.auto_id)
        formData.append('instructor_id', firmaPayload.value.instructor_id)
        formData.append('fecha_hora', firmaPayload.value.fecha_hora)
        formData.append('firma', file)

        const { data } = await axios.post('/api/asistencia', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        const idx = eventos.value.findIndex(
          (ev) => Number(ev.id || ev.programacionId) === Number(firmaPayload.value.programacion_id)
        )
        if (idx !== -1) {
          const asistenciaId = data?.id ?? data?.asistencia?.id ?? data?.data?.id ?? null
          const asistencia = data?.asistencia || data?.data?.asistencia || data?.data || data || {}
          eventos.value[idx].asistenciaRegistrada = true
          eventos.value[idx].asistenciaId = asistenciaId
          eventos.value[idx].instructorNombre = obtenerTexto(
            asistencia?.instructor?.nombre_completo,
            [asistencia?.instructor?.nombres, asistencia?.instructor?.apellido_paterno, asistencia?.instructor?.apellido_materno].filter(Boolean).join(' '),
            asistencia?.instructor_nombre,
            eventos.value[idx].instructorNombre
          )
          eventos.value[idx].vehiculoNombre = obtenerTexto(
            asistencia?.auto?.placa,
            asistencia?.auto_placa,
            asistencia?.auto_id,
            eventos.value[idx].vehiculoNombre
          )
          eventos.value[idx].fechaHoraAsistencia = asistencia?.fecha_hora || firmaPayload.value.fecha_hora || null
          eventos.value[idx].classNames = ['my-event']
        }

        mostrarSnackbar('Firma registrada correctamente')
        cerrarFirma()
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || 'No se pudo registrar la firma'
        mostrarSnackbar(msg, 'error')
      } finally {
        firmaLoading.value = false
      }
    }

    const extraerUrlFirma = (payload) => {
      let source = payload

      if (typeof source === 'string') {
        try {
          source = JSON.parse(source)
        } catch (_) {
          return source.split('\\/').join('/')
        }
      }

      const url = source?.url || source?.data?.url || source?.firma_url || source?.data?.firma_url || ''
      return typeof url === 'string' ? url.split('\\/').join('/') : ''
    }

    const verFirma = async (event) => {
      const asistenciaId = event.extendedProps?.asistenciaId
      const programacionId = event.extendedProps?.id || event.id || event.extendedProps?.programacionId

      firmaPreviewAsistenciaId.value = asistenciaId || null
      firmaPreviewProgramacionId.value = programacionId || null
      firmaPreviewLoading.value = true
      firmaPreviewError.value = ''
      firmaPreviewUrl.value = ''
      firmaPreviewDetalle.value = construirDetalleAsistencia({
        fallback: {
          instructor: event.extendedProps?.instructorNombre,
          vehiculo: event.extendedProps?.vehiculoNombre,
          fechaHora: event.extendedProps?.fechaHoraAsistencia,
        },
      })
      dialogFirmaPreview.value = true

      if (!asistenciaId) {
        firmaPreviewLoading.value = false
        firmaPreviewError.value = 'No se encontró una asistencia asociada a esta clase.'
        return
      }

      try {
        const { data } = await axios.get(`/api/asistencia/${asistenciaId}/firma`)
        firmaPreviewUrl.value = extraerUrlFirma(data)
        firmaPreviewDetalle.value = construirDetalleAsistencia({
          asistencia: data?.asistencia || data?.data?.asistencia || data?.data || data,
          fallback: {
            instructor: event.extendedProps?.instructorNombre,
            vehiculo: event.extendedProps?.vehiculoNombre,
            fechaHora: event.extendedProps?.fechaHoraAsistencia,
          },
        })

        if (!firmaPreviewUrl.value) {
          throw new Error('No se recibió una URL válida de firma.')
        }
      } catch (error) {
        firmaPreviewError.value = error?.response?.data?.message || 'No se pudo cargar la firma.'
      } finally {
        firmaPreviewLoading.value = false
      }
    }

    const cerrarFirmaPreview = () => {
      dialogFirmaPreview.value = false
      firmaPreviewLoading.value = false
      firmaPreviewUrl.value = ''
      firmaPreviewError.value = ''
      firmaPreviewAsistenciaId.value = null
      firmaPreviewProgramacionId.value = null
      firmaPreviewDetalle.value = {
        instructor: 'No disponible',
        vehiculo: 'No disponible',
        fechaHora: 'No disponible',
      }
    }

    const eliminarAsistencia = async ({ asistenciaId, programacionId }) => {

      if (!asistenciaId) {
        mostrarSnackbar('No se encontró una asistencia para eliminar.', 'warning')
        return
      }

      asistenciaDeletingId.value = programacionId
      try {
        await axios.delete(`/api/asistencia/${asistenciaId}`)

        const idx = eventos.value.findIndex((ev) => Number(ev.id || ev.programacionId) === Number(programacionId))
        if (idx !== -1) {
          eventos.value[idx].asistenciaRegistrada = false
          eventos.value[idx].asistenciaId = null
          eventos.value[idx].instructorNombre = null
          eventos.value[idx].vehiculoNombre = null
          eventos.value[idx].fechaHoraAsistencia = null
          eventos.value[idx].classNames = ['my-event']
        }

        mostrarSnackbar('Asistencia eliminada correctamente.')
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || 'No se pudo eliminar la asistencia'
        mostrarSnackbar(msg, 'error')
      } finally {
        asistenciaDeletingId.value = null
      }
    }

    const eliminarAsistenciaPreview = async () => {
      if (!firmaPreviewAsistenciaId.value || !firmaPreviewProgramacionId.value) {
        mostrarSnackbar('No se encontró una asistencia para eliminar.', 'warning')
        return
      }

      await eliminarAsistencia({
        asistenciaId: firmaPreviewAsistenciaId.value,
        programacionId: firmaPreviewProgramacionId.value,
      })

      const idx = eventos.value.findIndex(
        (ev) => Number(ev.id || ev.programacionId) === Number(firmaPreviewProgramacionId.value)
      )
      if (idx !== -1 && !eventos.value[idx].asistenciaRegistrada) {
        cerrarFirmaPreview()
      }
    }

    const dialogCancelacion = ref(false)
    const cancelacionLoading = ref(false)
    const cancelacionMotivo = ref(null)
    const cancelacionComentario = ref('')
    const cancelacionProgramacionId = ref(null)
    const motivosCancelacion = ref([])
    const cancelacionEvento = ref(null)
    const cancelacionesPendientes = ref([])
    const dialogInasistencia = ref(false)
    let resolverInasistencia = null

    const preguntarInasistencia = () => new Promise((resolve) => {
      resolverInasistencia = resolve
      dialogInasistencia.value = true
    })

    const responderInasistencia = (valor) => {
      dialogInasistencia.value = false
      if (resolverInasistencia) {
        resolverInasistencia(valor)
        resolverInasistencia = null
      }
    }


    const fetchMotivosCancelacion = async () => {
      if (motivosCancelacion.value.length > 0) return
      try {
        const { data } = await axios.get('/api/motivos-cancelacion')
        motivosCancelacion.value = data
      } catch (error) {
        mostrarSnackbar('Error al cargar motivos de cancelación', 'error')
      }
    }

    const abrirCancelacion = async (event) => {
      cancelacionProgramacionId.value = event.extendedProps?.programacionId || null
      cancelacionEvento.value = event
      cancelacionMotivo.value = null
      cancelacionComentario.value = ''
      await fetchMotivosCancelacion()
      dialogCancelacion.value = true
    }

    const cerrarCancelacion = () => {
      dialogCancelacion.value = false
      cancelacionProgramacionId.value = null
      cancelacionEvento.value = null
      cancelacionMotivo.value = null
      cancelacionComentario.value = ''
    }

    const confirmarCancelacion = async () => {
      if (!cancelacionMotivo.value || !cancelacionProgramacionId.value) return

      let crearAsistenciaInasistencia = false
      try {
        const { data } = await axios.post('/api/cancelacion/verificar-limite', {
          programacion_id: cancelacionProgramacionId.value,
          motivo_cancelacion_id: cancelacionMotivo.value,
        })
        if (data?.excede_limite_alumno) {
          crearAsistenciaInasistencia = await preguntarInasistencia()
        }
      } catch (error) {
        console.error(error)
        mostrarSnackbar('No se pudo verificar el límite de cancelaciones del alumno', 'error')
        return
      }

      cancelacionesPendientes.value.push({
        programacion_id: cancelacionProgramacionId.value,
        motivo_cancelacion_id: cancelacionMotivo.value,
        comentario: cancelacionComentario.value.trim() || undefined,
        crear_asistencia_inasistencia: crearAsistenciaInasistencia,
      })

      mostrarSnackbar('Clase marcada para cancelación. Se aplicará al guardar.')
      cerrarCancelacion()
    }

    const guardarProgramacion = async () => {
      const matriculaId = route.params.matriculaId

      // 1) Cancelaciones PRIMERO. El backend puede auto-generar una
      //    asistencia "por inasistencia" en la 2da cancelación del alumno
      //    para esa clase, lo cual luego bloquea la creación de una nueva
      //    programación de la misma clase. Si el sync corriera primero,
      //    la 3ra programación se crearía antes de que la asistencia
      //    quedara registrada y burlaría el reglamento.
      let asistenciaGeneradaPorInasistencia = false
      // IDs de programaciones canceladas a quitar del calendario al final.
      const idsCanceladasSimples = new Set()
      // IDs de programaciones canceladas que generaron asistencia automática
      // → quedan en el calendario marcadas como INASISTENCIA.
      const idsCanceladasConAsistencia = new Set()

      if (cancelacionesPendientes.value.length > 0) {
        try {
          for (const cancelacion of cancelacionesPendientes.value) {
            const { data } = await axios.post('/api/cancelacion', cancelacion)
            if (data?.asistencia_generada) {
              asistenciaGeneradaPorInasistencia = true
              idsCanceladasConAsistencia.add(cancelacion.programacion_id)
              mostrarSnackbar(
                data?.message ||
                  'Esta es la 2da cancelación del alumno para esta clase. Se registró automáticamente como clase realizada (inasistencia).',
                'warning',
                8000,
              )
            } else {
              idsCanceladasSimples.add(cancelacion.programacion_id)
            }
          }
          cancelacionesPendientes.value = []
          if (!asistenciaGeneradaPorInasistencia) {
            mostrarSnackbar('Cancelaciones aplicadas correctamente.')
          }
        } catch (e) {
          console.error(e)
          const msg = e?.response?.data?.message || 'Error al cancelar clases'
          mostrarSnackbar(msg, 'error')
          return
        }
      }

      // 2) Sync de programaciones. Excluimos del payload los eventos
      //    cuya programación ya fue cancelada en el paso anterior: el
      //    backend ya las marcó y no deben sincronizarse (ni en update
      //    ni en delete). Si la 2da cancelación generó una asistencia,
      //    el backend rechaza la creación de una nueva programación
      //    para esa clase con un 422.
      const programacionesCanceladas = new Set([
        ...idsCanceladasSimples,
        ...idsCanceladasConAsistencia,
      ])
      try {
        const clases = eventos.value
          .filter((ev) => !ev.readOnly)
          .filter((ev) => !ev.programacionId || !programacionesCanceladas.has(ev.programacionId))
          .map((ev) => ({
            id: ev.claseId,
            inicio: ev.start,
            fin: ev.end,
            circuito: !!ev.circuito,
          }))
        await store.dispatch('alumno/guardarProgramacionClases', {
          matriculaId,
          clases,
          circuito: false,
        })

        const matricula = await store.dispatch('alumno/fetchMatriculaById', matriculaId)
        const programaciones = matricula?.programaciones || []

        // 3) Conectar eventos nuevos (sin programacionId) con la
        //    programación recién creada.
        for (const prog of programaciones) {
          if (prog.cancelacion && !prog.asistencia) continue
          const idx = eventos.value.findIndex(
            (ev) => !ev.readOnly && ev.claseId === prog.clase_id && !ev.programacionId
          )
          if (idx !== -1) {
            eventos.value[idx].id = prog.id
            eventos.value[idx].programacionId = prog.id
            // Una vez guardada, la clase ya no se puede mover en el
            // calendario; cualquier cambio de horario debe hacerse
            // cancelando y reprogramando.
            eventos.value[idx].editable = false
            eventos.value[idx].startEditable = false
            eventos.value[idx].durationEditable = false
          }
          // Reflejar inmediatamente las asistencias (manuales o
          // auto-generadas) para que el botón de cancelar/firmar y el
          // chip INASISTENCIA respondan sin recargar la página.
          if (prog.asistencia) {
            const eIdx = eventos.value.findIndex(
              (ev) => !ev.readOnly && ev.programacionId === prog.id
            )
            if (eIdx !== -1) {
              eventos.value[eIdx].asistenciaRegistrada = true
              eventos.value[eIdx].asistenciaId = prog.asistencia.id || null
              eventos.value[eIdx].generadoPorInasistencias = !!prog.asistencia.generado_por_inasistencias
            }
          }
        }

        // 4) Quitar del calendario los eventos cancelados sin asistencia
        //    auto-generada. Los que sí generaron asistencia se quedan
        //    visibles con el chip INASISTENCIA. Los liberados vuelven al
        //    panel izquierdo de "Clases Disponibles" para que el alumno
        //    pueda reprogramar la clase (1ra vez según reglamento).
        if (idsCanceladasSimples.size > 0) {
          const eventosLiberados = eventos.value.filter(
            (ev) => !ev.readOnly && idsCanceladasSimples.has(ev.programacionId),
          )
          eventos.value = eventos.value.filter(
            (ev) => ev.readOnly || !idsCanceladasSimples.has(ev.programacionId),
          )
          for (const ev of eventosLiberados) {
            liberarClasePredefinida(ev)
          }
        }

        if (!asistenciaGeneradaPorInasistencia) {
          mostrarSnackbar('Programación guardada correctamente.')
        }
      } catch (e) {
        console.error(e)
        const msg = e?.response?.data?.message || 'Error al guardar programación'
        mostrarSnackbar(msg, 'error')
      }
    }

    const programarClase = ({ numero, startDate, omitirValidacionPago = false }) => {
      if (!numero || !(startDate instanceof Date) || Number.isNaN(startDate.getTime())) {
        return
      }

      if (numero !== clasesPredefinidas.value[0]?.numero) {
        mostrarSnackbar(`Debes programar primero la clase ${clasesPredefinidas.value[0]?.numero}.`, 'error')
        return
      }

      const numeroClase = Number(numero)
      if (!omitirValidacionPago && claseDebePagar.value > 0 && numeroClase > claseDebePagar.value) {
        requiereConfirmacionPago.value = true
        programacionPendiente.value = { numero, startDate }
        dialogConfirmacionPago.value = true
        return
      }

      const idxClase = clasesPredefinidas.value.findIndex((c) => c.numero === numero)
      if (idxClase === -1) return

      const descripcion = clasesPredefinidas.value[idxClase].descripcion
      const duracion = parseInt(clasesPredefinidas.value[idxClase].duracion)
      const claseId = clasesPredefinidas.value[idxClase].id

      const start = new Date(startDate)
      start.setSeconds(0, 0)
      const end = new Date(start.getTime() + duracion * 60000)

      if (hayConflicto(start.getTime(), end.getTime())) {
        mostrarSnackbar('Ya hay una clase programada en este horario.', 'error')
        return
      }

      eventos.value.push({
        title: `Clase ${numero}`,
        descripcion,
        numero,
        start: toUTCString(start),
        end: toUTCString(end),
        originalStart: toUTCString(start),
        duracion,
        claseId,
        readOnly: false,
        classNames: ['my-event'],
        circuito: false,
        asistenciaRegistrada: false,
        asistenciaId: null,
        kilometrajeInicial: null,
        kilometrajeFinal: null,
        kilometrajeCompleto: false,
        instructorNombre: null,
        vehiculoNombre: null,
        fechaHoraAsistencia: null,
        textColor: '#000',
      })

      clasesPredefinidas.value.splice(idxClase, 1)
    }

    const cancelarProgramacionPendiente = () => {
      programacionPendiente.value = null
      dialogConfirmacionPago.value = false
    }

    const confirmarProgramacionPendiente = () => {
      if (!programacionPendiente.value) return
      programarClase({ ...programacionPendiente.value, omitirValidacionPago: true })
      programacionPendiente.value = null
      dialogConfirmacionPago.value = false
    }

    const cargarProgramacionesOtros = async ({ inicio = null, fin = null } = {}) => {
      const params = {}
      if (inicio) params.fecha_inicio = toUTCString(inicio)
      if (fin) params.fecha_fin = toUTCString(fin)

      try {
        const todas = await store.dispatch('alumno/fetchProgramaciones', params)
        const matriculaActualId = parseInt(route.params.matriculaId)

        eventos.value = eventos.value.filter((ev) => !(ev.readOnly && ev.esProgramacion))

        for (const prog of todas) {
          if (prog.cancelacion) continue
          const progMatriculaId = prog.matricula_id ?? prog.matricula?.id
          if (progMatriculaId === matriculaActualId) continue

          const start = parseBackendDate(prog.fecha_hora_inicio)
          const end = parseBackendDate(prog.fecha_hora_fin)

          const alumno = prog.matricula?.alumno || {}
          const nombreAlumno = `${alumno.nombres || ''} ${alumno.apellido_paterno || ''} ${alumno.apellido_materno || ''}`.trim() || 'Otro alumno'

          const clase = prog.clase || clasesCurso.find(c => c.id === prog.clase_id) || {}
          const descr = clase.descripcion || ''
          const numeroClase = clase.numero || String(clasesCurso.findIndex(c => c.id === prog.clase_id) + 1)

          eventos.value.push({
            title: nombreAlumno,
            descripcion: descr,
            numero: numeroClase,
            start,
            end,
            originalStart: toUTCString(start),
            duracion: (end.getTime() - start.getTime()) / 60000,
            claseId: prog.clase_id || prog.clase?.id || null,
            programacionId: prog.id,
            esProgramacion: true,
            readOnly: true,
            classNames: ['other-event'],
            editable: false,
            textColor: '#fff',
            nombreCompleto: nombreAlumno,
          })
        }
      } catch (e) {
        console.error('Error cargando programaciones de otros alumnos', e)
      }
    }

    const calendarEvents = computed(() => eventos.value)

    watch(calendarEvents, (newEvents) => {
      const api = calendarRef.value?.getApi()
      if (api) {
        api.removeAllEvents()
        newEvents.forEach((ev) => api.addEvent(ev))
      }
    })

    const calendarOptions = computed(() => ({
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: 'timeGridWeek',
      timeZone: 'America/Lima',
      locale: esLocale,
      firstDay: 1,
      selectable: true,
      editable: true,
      droppable: true,
      events: calendarEvents.value,
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'timeGridWeek,timeGridDay'
      },
      allDaySlot: false,
      height: '80vh',
      expandRows: true,
      slotDuration: '00:15:00',
      slotLabelInterval: '01:00',
      slotMinTime: '05:00:00',
      slotMaxTime: '19:00:00',
      slotLabelFormat: { hour: 'numeric', minute: '2-digit', hour12: true },
      eventTimeFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true // Asegura el uso de AM/PM
      },

      dateClick(info) {
        info.view.calendar.changeView('timeGridDay', info.dateStr)
      },

      eventClick(info) {
        if (info.view.type === 'dayGridMonth') {
          info.view.calendar.changeView('timeGridDay', info.event.start)
        }
      },

      datesSet(info) {
        const api = calendarRef.value?.getApi()
        if (api) api.setOption('editable', info.view.type !== 'dayGridMonth')

        cargarProgramacionesOtros({
          inicio: info.start,
          fin: info.end,
        })
      },

      eventReceive(info) {
        if (info.view.type === 'dayGridMonth') {
          mostrarSnackbar('Solo puedes programar en la vista Semana o Día.', 'error')
          info.event.remove()
          return
        }

        const numero = info.event.title
        const start = new Date(info.event.start)
        info.event.remove()

        programarClase({ numero, startDate: start })
      },

      eventDrop(info) {
        if (info.event.extendedProps.readOnly) {
          info.revert()
          return
        }
        // No permitir mover programaciones ya guardadas: cualquier cambio
        // de horario debe pasar por cancelar y reprogramar (regla del
        // reglamento sobre máximo 1 reprogramación por clase).
        if (info.event.extendedProps.programacionId) {
          mostrarSnackbar(
            'Esta clase ya fue guardada. Para cambiar el horario, cancélala y reprogramala.',
            'warning',
          )
          info.revert()
          return
        }
        if (info.view.type === 'dayGridMonth') {
          mostrarSnackbar('Solo puedes reprogramar en la vista Semana o Día.', 'error')
          info.revert()
          return
        }

        const movedEvent = info.event
        const newStart = new Date(movedEvent.start)
        newStart.setSeconds(0, 0)

        const idx = eventos.value.findIndex(
          (ev) => ev.title === movedEvent.title && ev.originalStart === movedEvent.extendedProps.originalStart
        )
        const duracion = eventos.value[idx]?.duracion || 45
        const newEnd = new Date(newStart.getTime() + duracion * 60000)

        const copia = [...eventos.value]
        if (idx !== -1) copia.splice(idx, 1)

        const conflicto = copia.some((ev) => {
          const evStart = new Date(ev.start).getTime()
          const evEnd = new Date(ev.end).getTime()
          return newStart.getTime() < evEnd && newEnd.getTime() > evStart
        })
        if (conflicto) {
          mostrarSnackbar('Este horario ya está ocupado.', 'error')
          info.revert()
          return
        }

        if (idx !== -1) {
          eventos.value[idx].start = toUTCString(newStart)
          eventos.value[idx].end = toUTCString(newEnd)
        }
        movedEvent.setStart(newStart)
        movedEvent.setEnd(newEnd)
      }
    }))

    watch(dialogFirma, (abierto) => {
      if (!abierto && typeof window.SetTabletState === 'function') {
        window.SetTabletState(0, topazTimer.value)
      }
    })

    onMounted(async () => {
      try {
        const matriculaId = route.params.matriculaId
        const matricula = await store.dispatch('alumno/fetchMatriculaById', matriculaId)

        const analisisMatricula = matricula?.analisis?.matriculas?.find(
          (item) => Number(item.id) === Number(matriculaId)
        )
        claseDebePagar.value = Number(analisisMatricula?.clase_debe_pagar ?? 0)
        requiereConfirmacionPago.value = false

        const alumno = matricula?.alumno || {}
        const nombre = `${alumno.nombres || ''} ${alumno.apellido_paterno || ''} ${alumno.apellido_materno || ''}`.trim()
        alumnoInfo.value = {
          nombre: nombre || 'Alumno',
          dni: alumno.numero_documento || '',
          celular: alumno.celular || ''
        }

        clasesCurso = matricula?.curso?.clases || []
        const esCuantificable = matricula?.curso?.cuantificable === 1
        if (esCuantificable && clasesCurso.length === 1) {
          const cantidad = parseInt(matricula.cantidad) || 0
          const base = clasesCurso[0]
          clasesPredefinidas.value = Array.from({ length: cantidad }, (_, i) => ({
            numero: String(i + 1),
            id: base.id,
            descripcion: base.descripcion,
            duracion: base.duracion,
          }))
        } else {
          clasesPredefinidas.value = clasesCurso.map((c, i) => ({
            numero: String(i + 1),
            id: c.id,
            descripcion: c.descripcion,
            duracion: c.duracion,
          }))
        }

        const programaciones = matricula?.programaciones || []
        for (const prog of programaciones) {
          // Si la programación está cancelada Y no tiene asistencia, no se
          // muestra. Pero si tiene una asistencia auto-generada por la 2da
          // cancelación del alumno (cancelacion + asistencia), sí la
          // mostramos para que quede visible que la clase fue marcada
          // como inasistencia (no se puede reprogramar más).
          if (prog.cancelacion && !prog.asistencia) continue
          const idxC = clasesPredefinidas.value.findIndex((c) => c.id === prog.clase_id)
          if (idxC === -1) continue

          const claseInfo = clasesPredefinidas.value[idxC]
          const start = parseBackendDate(prog.fecha_hora_inicio)
          const end = parseBackendDate(prog.fecha_hora_fin)

          eventos.value.push({
            title: `Clase ${claseInfo.numero}`,
            descripcion: claseInfo.descripcion,
            numero: claseInfo.numero,
            start,                           // Date local 07:00
            end,                             // Date local 07:45
            originalStart: toUTCString(start),
            duracion: claseInfo.duracion,
            claseId: claseInfo.id,
            classNames: ['my-event'],
            circuito: !!prog.circuito,
            id: prog.id,
            programacionId: prog.id,
            // Las programaciones ya guardadas no se pueden mover por
            // drag&drop. Para reprogramar hay que cancelar y volver a
            // programar (regla del reglamento: 1 reprogramación por clase).
            editable: false,
            startEditable: false,
            durationEditable: false,
            asistenciaRegistrada: !!prog.asistencia,
            asistenciaId: prog.asistencia?.id || null,
            kilometrajeInicial: prog.asistencia?.kilometraje_inicial ?? null,
            kilometrajeFinal: prog.asistencia?.kilometraje_final ?? null,
            kilometrajeCompleto:
              prog.asistencia?.kilometraje_inicial != null &&
              prog.asistencia?.kilometraje_final != null,
            generadoPorInasistencias: !!prog.asistencia?.generado_por_inasistencias,
            instructorNombre: obtenerTexto(
              prog.asistencia?.instructor?.nombre_completo,
              [prog.asistencia?.instructor?.nombres, prog.asistencia?.instructor?.apellido_paterno, prog.asistencia?.instructor?.apellido_materno].filter(Boolean).join(' '),
              prog.asistencia?.instructor_nombre
            ),
            vehiculoNombre: obtenerTexto(
              prog.asistencia?.auto?.placa,
              prog.asistencia?.auto_placa,
              prog.asistencia?.auto_id
            ),
            fechaHoraAsistencia: prog.asistencia?.fecha_hora || null,
            textColor: '#000',
          })

          clasesPredefinidas.value.splice(idxC, 1)
        }

        try {
          await store.dispatch('evento/fetchEventos')
          const extras = store.getters['evento/eventos']
          for (const ev of extras) {
            eventos.value.push({
              id: ev.id,
              title: ev.titulo,
              descripcion: ev.descripcion,
              start: parseBackendDate(ev.fecha_inicio),
              end: ev.fecha_fin ? parseBackendDate(ev.fecha_fin) : null,
              readOnly: true,
              classNames: ['other-event'],
              editable: false,
              textColor: '#fff',
            })
          }
        } catch (e) {
          console.error('Error cargando eventos', e)
        }
      } catch (e) {
        console.error('Error cargando clases', e)
      }

      nextTick(() => {
        const api = calendarRef.value?.getApi()
        if (api) {
          cargarProgramacionesOtros({
            inicio: api.view.currentStart,
            fin: api.view.currentEnd,
          })
        }
      })

      nextTick(() => {
        if (externalEvents.value) {
          new Draggable(externalEvents.value, {
            itemSelector: '.external-event',
            eventData(el) {
              const duracion = parseInt(el.getAttribute('data-duracion'))
              return {
                title: el.getAttribute('data-title'),
                duration: { minutes: Number.isFinite(duracion) ? duracion : 60 },
                extendedProps: {
                  descripcion: el.getAttribute('data-descripcion'),
                  duracion: duracion,
                  claseId: parseInt(el.getAttribute('data-id')),
                  numero: el.getAttribute('data-title'),
                },
              }
            },
          })
        }
        setTimeout(() => calendarRef.value?.getApi().updateSize(), 300)
      })
    })

    return {
      calendarRef,
      externalEvents,
      calendarOptions,
      eventos,
      clasesPredefinidas,
      snackbar,
      abrirDialogoDuracion,
      dialogoDuracion,
      dialogConfirmacionPago,
      nuevaDuracion,
      actualizarDuracion,
      guardarProgramacion,
      requiereConfirmacionPago,
      claseDebePagar,
      cancelarProgramacionPendiente,
      confirmarProgramacionPendiente,
      alumnoInfo,
      generarTooltip,
      toggleCircuito,
      dialogFirma,
      firmaLoading,
      signatureCanvas,
      firmaPayload,
      abrirFirma,
      iniciarTopaz,
      limpiarFirma,
      cerrarFirma,
      guardarFirma,
      reglaKilometrajeInicial,
      reglaKilometrajeFinal,
      dialogKilometraje,
      kilometrajeLoading,
      kilometrajePayload,
      abrirKilometraje,
      cerrarKilometraje,
      guardarKilometraje,
      asistenciaDeletingId,
      verFirma,
      dialogFirmaPreview,
      firmaPreviewLoading,
      firmaPreviewUrl,
      firmaPreviewError,
      firmaPreviewAsistenciaId,
      firmaPreviewProgramacionId,
      firmaPreviewDetalle,
      formatDateTime,
      cerrarFirmaPreview,
      eliminarAsistenciaPreview,
      dialogCancelacion,
      cancelacionLoading,
      cancelacionMotivo,
      cancelacionComentario,
      cancelacionProgramacionId,
      motivosCancelacion,
      abrirCancelacion,
      cerrarCancelacion,
      confirmarCancelacion,
      dialogInasistencia,
      responderInasistencia,
    }
  },
})
</script>

<style scoped>
.calendar {
  max-width: 100%;
  margin: 0 auto;
}

.external-event {
  cursor: move;
  user-select: none;
  font-size: 0.75rem;
}

.event-content {
  width: 100%;
  height: 100%;
  padding: 2px;
  overflow: hidden;
}

.event-info {
  font-size: 0.7rem;
  line-height: 1.1;
  white-space: normal !important; /* Fuerza a que el texto baje de línea */
  word-break: break-word;
}

.event-actions {
  margin-top: 2px;
}

/* Quitamos el padding interno extra que pone FullCalendar para aprovechar todo el cuadro */
:deep(.fc-event-main) {
  padding: 0 !important;
}

:deep(.my-event) {
  background-color: #f2cd14 !important;
  border-color: #f2cd14 !important;
  color: #000 !important;
}

:deep(.other-event) {
  background-color: #424242 !important;
  border-color: #424242 !important;
  color: #fff !important;
}

.signature-canvas {
  border: 1px dashed #9e9e9e;
  width: 100%;
  max-width: 100%;
}

.firma-preview {
  display: block;
  width: 100%;
  max-height: 380px;
  object-fit: contain;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin: 0 auto;
}
</style>
